import api from './api'
import type {
  CreateMatchRequest,
  CreateMatchResponse,
  MatchJobStatus,
  MatchResultsResponse,
  ListMatchesResponse,
  SubmitFeedbackRequest,
  AIHealthStatus,
  PreviewResponse,
} from '@/types'

export interface GetMatchResultsParams {
  matchId: string
  page?: number
  limit?: number
  minScore?: number
  excludeProspects?: boolean
}

const matchingService = {
  /**
   * Preview AI matching results without consuming credits
   * @param request - Job description and filters
   * @returns Preview response with candidate pool, statistics, and pricing
   */
  async previewMatch(request: CreateMatchRequest): Promise<PreviewResponse['data']> {
    const response = await api.post('/ai-matching/preview', request)
    return response.data.data
  },

  /**
   * Create a new AI matching request (Async - returns job ID)
   * @param request - Job description and filters
   * @returns Match job creation response with job ID and pending status
   */
  async createMatch(request: CreateMatchRequest): Promise<CreateMatchResponse> {
    const response = await api.post('/ai-matching/match', request)
    return response.data.data
  },

  /**
   * Get match job status (Polling endpoint)
   * @param jobId - Match job ID from createMatch
   * @returns Job status with results if completed
   */
  async getMatchJobStatus(jobId: string): Promise<MatchJobStatus> {
    const response = await api.get(`/ai-matching/jobs/${jobId}`)
    return response.data.data
  },

  /**
   * Get match results for a job description
   * @param params - Match ID and pagination/filtering options
   * @returns Paginated match results with candidate data
   */
  async getMatchResults(params: GetMatchResultsParams): Promise<MatchResultsResponse> {
    const { matchId, page = 1, limit = 20, minScore, excludeProspects } = params
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(minScore && { minScore: minScore.toString() }),
      ...(excludeProspects && { excludeProspects: 'true' }),
    })

    const response = await api.get(`/ai-matching/match/${matchId}?${queryParams}`)
    return response.data.data
  },

  /**
   * Submit feedback on a match result
   * ENDPOINT: /api/ai-matching/matches/:matchId/candidates/:candidateId/feedback
   * @param matchId - AI match job ID (aiMatchJobId, same as the jobId from POST /match)
   * @param candidateId - Candidate ID
   * @param feedback - Feedback data (RELEVANT, NOT_RELEVANT, SHORTLISTED)
   * @returns Success message
   *
   * Note: When feedback is 'SHORTLISTED', the backend automatically saves the candidate
   */
  async submitFeedback(
    matchId: string,
    candidateId: string,
    feedback: Omit<SubmitFeedbackRequest, 'candidateId'>
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post(
      `/ai-matching/matches/${matchId}/candidates/${candidateId}/feedback`,
      feedback
    )
    return response.data.data
  },

  /**
   * List all AI match jobs for the current recruiter
   * @param params - Pagination options
   * @returns Paginated list of AI match summaries
   */
  async listMatches(params?: { page?: number; limit?: number }): Promise<ListMatchesResponse> {
    const { page = 1, limit = 20 } = params || {}
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    const response = await api.get(`/ai-matching/matches?${queryParams}`)
    return response.data.data
  },

  /**
   * Check AI service health status
   * @returns Health status of AI service and database
   */
  async checkHealth(): Promise<AIHealthStatus> {
    const response = await api.get('/ai-matching/health')
    return response.data
  },

  /**
   * Archive a match result
   * @param matchId - Match result ID
   * @returns Success response with message and matchId
   */
  async archiveMatchResult(matchId: string): Promise<{ success: boolean; data: { message: string; matchId: string } }> {
    const response = await api.patch(`/ai-matching/match/${matchId}/archive`)
    return response.data
  },

  /**
   * Delete a match result
   * @param matchId - Match result ID
   * @returns Success response with message and matchId
   */
  async deleteMatchResult(matchId: string): Promise<{ success: boolean; data: { message: string; matchId: string } }> {
    const response = await api.delete(`/ai-matching/match/${matchId}`)
    return response.data
  },
}

export default matchingService
